import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileDown, Copy, Trash2, Search, Plus } from 'lucide-react';
import { qd, po, money, apiError } from '../api';

const PO_STATUS = { draft: 'bg-slate-100 text-slate-600', issued: 'bg-blue-50 text-blue-700', sent: 'bg-emerald-50 text-emerald-700' };
const STATUSES = ['all', 'draft', 'issued', 'sent'];
const ENTS = ['all', 'uab', 'sia', 'e2'];

const downloadBlob = async (url, filename) => {
  const { data } = await po.get(url, { responseType: 'blob' });
  const u = URL.createObjectURL(new Blob([data]));
  const a = document.createElement('a');
  a.href = u; a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(u); }, 2000);
};

export default function PoOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState('all');
  const [ent, setEnt] = useState('all');
  const [search, setSearch] = useState('');

  const load = () => po.get('/orders').then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const del = async (o) => {
    if (!window.confirm(`Delete ${o.number || 'this draft'}? This cannot be undone.`)) return;
    try { await po.delete(`/orders/${o.id}`); load(); } catch (e) { alert(apiError(e)); }
  };
  const dup = async (o) => {
    try { const { data } = await po.post(`/orders/${o.id}/duplicate`); navigate(`/quote/po/${data.id}`); } catch (e) { alert(apiError(e)); }
  };

  const shown = orders.filter((o) => {
    if (status !== 'all' && o.status !== status) return false;
    if (ent !== 'all' && o.entity_id !== ent) return false;
    const s = search.trim().toLowerCase();
    return !s || (o.number || 'draft').toLowerCase().includes(s)
      || (o.supplier_snapshot?.name || 'tektelic').toLowerCase().includes(s)
      || (o.notes || '').toLowerCase().includes(s);
  });

  return (
    <div className="p-6 md:p-8" data-testid="po-orders-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-xl font-bold text-[#14233F]">Purchase Orders</h1>
        <div className="flex gap-1.5 items-center flex-wrap">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
              className="border border-slate-300 rounded pl-8 pr-3 py-1.5 text-sm w-44" data-testid="po-search" />
          </div>
          {ENTS.map((x) => (
            <button key={x} onClick={() => setEnt(x)}
              className={`px-2.5 py-1.5 text-xs font-medium rounded border uppercase ${ent === x ? 'bg-[#14233F] text-white border-[#14233F]' : 'bg-white text-slate-600 border-slate-200'}`}
              data-testid={`po-ent-${x}`}>{x}</button>
          ))}
          {STATUSES.map((x) => (
            <button key={x} onClick={() => setStatus(x)}
              className={`px-2.5 py-1.5 text-xs font-medium rounded border capitalize ${status === x ? 'bg-[#0F7D70] text-white border-[#0F7D70]' : 'bg-white text-slate-600 border-slate-200'}`}
              data-testid={`po-status-${x}`}>{x}</button>
          ))}
          <button onClick={() => navigate('/quote/po/new')} className="ml-2 inline-flex items-center gap-1.5 bg-[#0F7D70] text-white px-4 py-2 rounded text-sm font-semibold" data-testid="po-new-btn">
            <Plus className="h-4 w-4" /> New Purchase Order
          </button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3 font-semibold">Number</th>
              <th className="text-left px-4 py-3 font-semibold">Entity</th>
              <th className="text-left px-4 py-3 font-semibold">Supplier</th>
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-right px-4 py-3 font-semibold">Lines</th>
              <th className="text-right px-4 py-3 font-semibold">Grand total €</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((o) => (
              <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <Link to={`/quote/po/${o.id}`} className="mono font-semibold text-[#0F7D70] hover:underline" data-testid={`po-link-${o.number || o.id}`}>
                    {o.number || 'Draft'}
                  </Link>
                </td>
                <td className="px-4 py-3 mono text-xs text-slate-500 uppercase">{o.entity_id}</td>
                <td className="px-4 py-3 text-slate-700">{o.supplier_snapshot?.name || 'TEKTELIC Communications Inc.'}</td>
                <td className="px-4 py-3 mono text-xs text-slate-500">{o.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 mono text-right">{o.items?.length || 0}</td>
                <td className="px-4 py-3 mono text-right">{money(o.totals?.grand_total)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold capitalize ${PO_STATUS[o.status]}`}>{o.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2.5">
                    {o.status !== 'draft' && (
                      <button onClick={() => downloadBlob(`/orders/${o.id}/pdf`, `${o.number}_${(o.supplier_snapshot?.name || 'TEKTELIC').split(' ')[0].toUpperCase()}.pdf`)}
                        title="Download PDF" data-testid={`po-pdf-${o.number}`}>
                        <FileDown className="h-4 w-4 text-[#0F7D70] hover:text-[#14233F]" />
                      </button>
                    )}
                    <button onClick={() => dup(o)} title="Duplicate" data-testid={`po-dup-${o.number || o.id}`}>
                      <Copy className="h-4 w-4 text-slate-300 hover:text-[#0F7D70]" />
                    </button>
                    <button onClick={() => del(o)} title="Delete" data-testid={`po-del-${o.id}`}>
                      <Trash2 className="h-4 w-4 text-slate-300 hover:text-red-600" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400 text-sm">No purchase orders yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
